import { Button, ModalWindow } from '@cian/ui-kit';

import { type FC, type SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';

import { useCardOfferPublisherContext } from '@/microfrontends/listing/shared/context/selectors';
import { useCAForCard } from '@/microfrontends/listing/shared/hooks/card/useCAForCard';
import { formatPhone } from '@/microfrontends/listing/shared/utils/formatPhone';

import { notification } from '@/packages/notifications-center';
import { useUserQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';
import { PortalEventStoper } from '@/packages/shared-ui';
import { memoWithDisplayName } from '@/packages/utils';

import { ContactsModalContent } from './componentns/ContactsModalContent';

type ContactsModalContainerProps = {
  open: boolean;
  onClose(): void;
};

const ContactsModalContainer: FC<ContactsModalContainerProps> = ({ open, onClose }) => {
  const { phones, workHours, name, typeLabels } = useCardOfferPublisherContext();
  const notificationRef = useRef<number | string | undefined>();
  const phoneEntries = useMemo<{ raw: string; formatted: string }[]>(() => {
    return phones.map(phone => ({
      raw: `+${phone.countryCode}${phone.number}`,
      formatted: formatPhone(phone),
    }));
  }, [phones]);

  const onCopy = useCallback((_: string): void => {
    if (notificationRef.current === undefined || !notification.isActive(notificationRef.current)) {
      notificationRef.current = notification.success('Номер телефона скопирован');
    }
  }, []);

  const publisher = useMemo(() => {
    return `${typeLabels?.[0]} ${name}`.trim();
  }, [name, typeLabels]);

  return (
    <ModalWindow maxWidth={500} open={open} onClose={onClose}>
      <ContactsModalContent phones={phoneEntries} publisher={publisher} workHours={workHours} onCopy={onCopy} />
    </ModalWindow>
  );
};

type ContactsButtonProps = {
  onClick(e: SyntheticEvent): void;
};

const ContactsButton: FC<ContactsButtonProps> = ({ onClick }) => {
  const ca = useCAForCard();

  const { data: user } = useUserQuery();

  const handleButtonClick = useCallback(
    (e: SyntheticEvent): void => {
      e.stopPropagation();

      ca({ action: user?.isAgent ? 'Open_listing_agent_block' : 'Open_listing', category: 'Phones' }, { label: true });

      onClick(e);
    },
    [ca, onClick, user],
  );

  return (
    <Button fullWidth data-testid="publisher.phone-button" size="M" theme="fill_primary" onClick={handleButtonClick}>
      Позвонить
    </Button>
  );
};

export const ContactsButtonRenderer = memoWithDisplayName<Omit<ContactsButtonProps, 'onClick'> & { text?: string }>(
  props => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <>
        <ContactsButton {...props} onClick={() => setIsOpen(true)} />
        <PortalEventStoper>
          <ContactsModalContainer open={isOpen} onClose={() => setIsOpen(false)} />
        </PortalEventStoper>
      </>
    );
  },
  'ContactsButtonRenderer',
);
